import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Globe, Target, Eye, Award, Users, Zap, Mail, Phone, MessageCircle } from 'lucide-react';
import { teamMembers, globalLocations } from '../mock';

const AboutUs = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setFormSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
        setTimeout(() => setFormSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <>
      <Helmet>
        <title>About Eaglesway Group | International Business & Trade</title>
        <meta 
          name="description" 
          content="Learn about Eaglesway Group's mission, vision, and global operations across UAE, UK, and USA. Premium international trading and business solutions." 
        />
      </Helmet>

      <div className="min-h-screen bg-black text-white pt-20">
        {/* Hero Section */}
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2000"
              alt="About Eaglesway Group"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black via-black/90 to-black"></div>
          </div>

          <div className={`relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="mb-6 inline-flex items-center px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full">
              <Award className="w-4 h-4 text-[#D4AF37] mr-2" />
              <span className="text-[#D4AF37] text-sm font-medium">Premium International Business Group</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              About <span className="text-[#D4AF37]">Eaglesway Group</span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              A leading international business group providing global trading, procurement, logistics, and marketing services across UAE, UK, and USA
            </p>
          </div>
        </section>

        {/* Company Overview */}
        <section className="py-20 bg-gradient-to-b from-black to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  Who <span className="text-[#D4AF37]">We Are</span>
                </h2>
                <div className="space-y-4 text-gray-300 leading-relaxed">
                  <p>
                    Eaglesway Group is a premium international business conglomerate operating across three continents, specializing in global trading, procurement, logistics, and strategic marketing services.
                  </p>
                  <p>
                    With strategically positioned divisions in the UAE, United Kingdom, and United States, we serve as a trusted partner for businesses seeking reliable supply chain solutions, market access, and operational excellence.
                  </p>
                  <p>
                    Our multi-industry expertise spans oil & gas, industrial machinery, electronics, agricultural commodities, and international trade facilitation, enabling us to deliver comprehensive solutions tailored to diverse business needs.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <Card className="bg-gradient-to-br from-gray-900 to-black border-gray-800 p-6 text-center">
                  <div className="text-4xl font-bold text-[#D4AF37] mb-2">5+</div>
                  <div className="text-gray-400 text-sm">Years Experience</div>
                </Card>
                <Card className="bg-gradient-to-br from-gray-900 to-black border-gray-800 p-6 text-center">
                  <div className="text-4xl font-bold text-[#D4AF37] mb-2">3</div>
                  <div className="text-gray-400 text-sm">Continents</div>
                </Card>
                <Card className="bg-gradient-to-br from-gray-900 to-black border-gray-800 p-6 text-center">
                  <div className="text-4xl font-bold text-[#D4AF37] mb-2">4</div>
                  <div className="text-gray-400 text-sm">Global Offices</div>
                </Card>
                <Card className="bg-gradient-to-br from-gray-900 to-black border-gray-800 p-6 text-center">
                  <div className="text-4xl font-bold text-[#D4AF37] mb-2">24/7</div>
                  <div className="text-gray-400 text-sm">Support</div>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-20 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Mission */}
              <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-gray-800 hover:border-[#D4AF37] transition-all duration-500 overflow-hidden group">
                <div className="p-8">
                  <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Target className="w-8 h-8 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">Our Mission</h3>
                  <p className="text-gray-300 leading-relaxed">
                    To deliver exceptional value through reliable global trading, procurement, and logistics solutions while maintaining the highest standards of integrity, quality, and customer service across all markets we serve.
                  </p>
                </div>
              </Card>

              {/* Vision */}
              <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-gray-800 hover:border-[#D4AF37] transition-all duration-500 overflow-hidden group">
                <div className="p-8">
                  <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Eye className="w-8 h-8 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">Our Vision</h3>
                  <p className="text-gray-300 leading-relaxed">
                    To be the preferred international business partner, recognized globally for operational excellence, strategic market positioning, and innovative supply chain solutions that drive sustainable growth for our clients.
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-20 bg-gradient-to-b from-black to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Our Core <span className="text-[#D4AF37]">Values</span>
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                The principles that guide our operations and relationships
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="bg-gradient-to-b from-gray-900 to-black border border-gray-800 p-8 text-center hover:border-[#D4AF37] transition-all duration-300">
                <Globe className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">Global Excellence</h3>
                <p className="text-gray-400">
                  Delivering world-class service across all markets with consistent quality and reliability
                </p>
              </Card>

              <Card className="bg-gradient-to-b from-gray-900 to-black border border-gray-800 p-8 text-center hover:border-[#D4AF37] transition-all duration-300">
                <Award className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">Integrity & Trust</h3>
                <p className="text-gray-400">
                  Building lasting relationships through transparency, honesty, and ethical business practices
                </p>
              </Card>

              <Card className="bg-gradient-to-b from-gray-900 to-black border border-gray-800 p-8 text-center hover:border-[#D4AF37] transition-all duration-300">
                <Zap className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">Innovation & Agility</h3>
                <p className="text-gray-400">
                  Adapting quickly to market changes and leveraging technology for optimal solutions
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Leadership & Team */}
        <section className="py-24 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)', backgroundSize: '40px 40px'}}></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            
            {/* Executive Leadership */}
            <div className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                  Executive <span className="text-[#D4AF37]">Leadership</span>
                </h2>
              </div>

              {/* MELEYONE B. TSEGAYE - Founder - Centered */}
              <div className="flex justify-center">
                <div className="w-full md:w-1/3 bg-gradient-to-br from-gray-900 to-black border-2 border-[#D4AF37] rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-2 shadow-2xl">
                  <div className="relative h-80 overflow-hidden">
                    <img
                      src="https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/lo0la7dr_MELEYONE%20CID%20ID%20PHOTO.jpeg"
                      alt="Meleyone B. Tsegaye"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="inline-block bg-[#D4AF37] text-black text-xs font-bold px-3 py-1 rounded-full">
                        FOUNDER
                      </span>
                    </div>
                  </div>
                  <div className="p-6 text-center">
                    <h3 className="text-xl font-bold text-white mb-2">
                      MELEYONE B. TSEGAYE
                    </h3>
                    <p className="text-[#D4AF37] font-semibold mb-1 text-sm">
                      Founder & Group Chief Executive Officer
                    </p>
                    <p className="text-gray-400 text-sm">
                      Eaglesway Group
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Our Team Staff */}
            <div className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                  Our <span className="text-[#D4AF37]">Team</span>
                </h2>
              </div>

              {/* Team Members - 3 Profiles */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                
                {/* 1. SANA BEN AMEUR */}
                <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-gray-800 hover:border-[#D4AF37] rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-2">
                  <div className="relative h-80 overflow-hidden">
                    <img
                      src="https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/qixiqfzy_WhatsApp%20Image%202025-12-25%20at%202.59.57%20PM.jpeg"
                      alt="Sana Ben Ameur"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6 text-center">
                    <h3 className="text-xl font-bold text-white mb-2">
                      SANA BEN AMEUR
                    </h3>
                    <p className="text-[#D4AF37] font-semibold mb-1 text-sm">
                      Group International Business Manager
                    </p>
                    <p className="text-gray-400 text-sm">
                      Eaglesway Group
                    </p>
                  </div>
                </div>

                {/* 2. MERON DEREJE */}
                <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-gray-800 hover:border-[#D4AF37] rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-2">
                  <div className="relative h-80 overflow-hidden">
                    <img
                      src="https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/3v6vpyfo_MERON%20DEREJE.jpg"
                      alt="Meron Dereje"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6 text-center">
                    <h3 className="text-xl font-bold text-white mb-2">
                      MERON DEREJE
                    </h3>
                    <p className="text-[#D4AF37] font-semibold mb-1 text-sm">
                      Global Business Development
                    </p>
                    <p className="text-gray-400 text-sm">
                      Eaglesway Group
                    </p>
                  </div>
                </div>

                {/* 3. LUCKWINDER SINGH */}
                <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-gray-800 hover:border-[#D4AF37] rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-2">
                  <div className="relative h-80 overflow-hidden">
                    <img
                      src="https://customer-assets-wrfwihn1.emergentagent.net/job_premium-sourcing-7/artifacts/879c21a221a572e1_28EEE83B-B13A-40BB-B88C-5F37AC3B44B1.png"
                      alt="Luckwinder Singh"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6 text-center">
                    <h3 className="text-xl font-bold text-white mb-2">
                      LUCKWINDER SINGH
                    </h3>
                    <p className="text-[#D4AF37] font-semibold mb-1 text-sm">
                      UK Sales & Business Development Manager
                    </p>
                    <p className="text-gray-400 text-sm">
                      Eaglesway Group
                    </p>
                  </div>
                </div>

              </div>
            </div>

            {/* 3. OUR TEAMS - Department Cards */}
            <div>
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                  Our <span className="text-[#D4AF37]">Teams</span>
                </h2>
                <p className="text-gray-400 text-lg max-w-3xl mx-auto">
                  Specialized departments supporting Eaglesway's international operations.
                </p>
              </div>

              {/* Department Cards - 3 columns, 2 rows */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                
                {/* Group Administration */}
                <div className="group bg-gradient-to-br from-gray-900 to-black border border-gray-800 hover:border-[#D4AF37] rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-white">Group Administration</h3>
                    <div className="w-8 h-8 flex items-center justify-center">
                      <div className="w-6 h-0.5 bg-[#D4AF37] transform group-hover:w-8 transition-all duration-300"></div>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm">Executive management and corporate governance</p>
                </div>

                {/* Global Sourcing & Procurement */}
                <div className="group bg-gradient-to-br from-gray-900 to-black border border-gray-800 hover:border-[#D4AF37] rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-white">Global Sourcing & Procurement</h3>
                    <div className="w-8 h-8 flex items-center justify-center">
                      <div className="w-6 h-0.5 bg-[#D4AF37] transform group-hover:w-8 transition-all duration-300"></div>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm">International supplier networks and product sourcing</p>
                </div>

                {/* Trade & Logistics */}
                <div className="group bg-gradient-to-br from-gray-900 to-black border border-gray-800 hover:border-[#D4AF37] rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-white">Trade & Logistics</h3>
                    <div className="w-8 h-8 flex items-center justify-center">
                      <div className="w-6 h-0.5 bg-[#D4AF37] transform group-hover:w-8 transition-all duration-300"></div>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm">Import-export operations and supply chain management</p>
                </div>

                {/* Business Development */}
                <div className="group bg-gradient-to-br from-gray-900 to-black border border-gray-800 hover:border-[#D4AF37] rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-white">Business Development</h3>
                    <div className="w-8 h-8 flex items-center justify-center">
                      <div className="w-6 h-0.5 bg-[#D4AF37] transform group-hover:w-8 transition-all duration-300"></div>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm">Strategic partnerships and market expansion</p>
                </div>

                {/* Marketing & Communications */}
                <div className="group bg-gradient-to-br from-gray-900 to-black border border-gray-800 hover:border-[#D4AF37] rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-white">Marketing & Communications</h3>
                    <div className="w-8 h-8 flex items-center justify-center">
                      <div className="w-6 h-0.5 bg-[#D4AF37] transform group-hover:w-8 transition-all duration-300"></div>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm">Brand management and corporate communications</p>
                </div>

                {/* Finance & Compliance */}
                <div className="group bg-gradient-to-br from-gray-900 to-black border border-gray-800 hover:border-[#D4AF37] rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-white">Finance & Compliance</h3>
                    <div className="w-8 h-8 flex items-center justify-center">
                      <div className="w-6 h-0.5 bg-[#D4AF37] transform group-hover:w-8 transition-all duration-300"></div>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm">Financial management and regulatory compliance</p>
                </div>

              </div>
            </div>

          </div>
        </section>

        {/* Global Presence Overview */}
        <section className="py-20 bg-gradient-to-b from-black to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Global <span className="text-[#D4AF37]">Presence</span>
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Strategic locations enabling seamless international operations
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {globalLocations.map((location) => (
                <Card
                  key={location.id}
                  className="bg-gradient-to-b from-gray-900 to-black border border-gray-800 hover:border-[#D4AF37] transition-all duration-300 p-6"
                >
                  <div className="text-4xl mb-4">{location.flag}</div>
                  <h3 className="text-lg font-bold text-white mb-1">{location.city}</h3>
                  <p className="text-gray-500 text-sm mb-3">{location.country}</p>
                  <p className="text-[#D4AF37] text-xs font-medium">{location.type}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="py-24 bg-black">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="border-2 border-[#D4AF37] rounded-3xl p-12 md:p-16 bg-gradient-to-b from-gray-900/50 to-black/50 relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)', backgroundSize: '40px 40px'}}></div>
              </div>

              <div className="relative z-10">
                <div className="text-center mb-10">
                  <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                    Ready to Partner with <span className="text-[#D4AF37]">Eaglesway Group</span>?
                  </h2>
                  <p className="text-gray-300 text-lg md:text-xl max-w-3xl mx-auto">
                    Whether you need global sourcing, logistics solutions, or marketing support, our team is ready to help your business thrive in international markets.
                  </p>
                </div>

                {/* Contact Form */}
                <form onSubmit={handleFormSubmit} className="max-w-2xl mx-auto space-y-6 mb-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Name */}
                    <div>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Your Name *"
                        required
                        className="w-full px-6 py-4 bg-black/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#D4AF37] focus:outline-none transition-colors duration-300"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Email Address *"
                        required
                        className="w-full px-6 py-4 bg-black/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#D4AF37] focus:outline-none transition-colors duration-300"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="Phone Number *"
                        required
                        className="w-full px-6 py-4 bg-black/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#D4AF37] focus:outline-none transition-colors duration-300"
                      />
                    </div>

                    {/* Company */}
                    <div>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder="Company Name"
                        className="w-full px-6 py-4 bg-black/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#D4AF37] focus:outline-none transition-colors duration-300"
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Your Message / Requirements *"
                      required
                      rows="4"
                      className="w-full px-6 py-4 bg-black/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#D4AF37] focus:outline-none transition-colors duration-300 resize-none"
                    ></textarea>
                  </div>

                  {/* Submit Button & WhatsApp */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button
                      type="submit"
                      disabled={formSubmitting}
                      className="flex-1 bg-[#D4AF37] hover:bg-[#B8941F] text-black font-semibold px-10 py-7 text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Mail className="w-5 h-5 mr-2" />
                      {formSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => window.open('https://wa.me/971525712721', '_blank')}
                      variant="outline"
                      className="flex-1 border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black font-semibold px-10 py-7 text-lg transition-all duration-300"
                    >
                      <MessageCircle className="w-5 h-5 mr-2" />
                      Chat on WhatsApp
                    </Button>
                  </div>

                  {/* Success Message */}
                  {formSuccess && (
                    <div className="bg-green-500/20 border border-green-500 text-green-500 px-6 py-4 rounded-lg text-center">
                      ✓ Message sent successfully! We'll get back to you soon.
                    </div>
                  )}
                </form>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-gray-400">
                  <div className="flex items-center">
                    <Phone className="w-5 h-5 text-[#D4AF37] mr-2" />
                    <span>+971 52 571 2721</span>
                  </div>
                  <div className="flex items-center">
                    <Mail className="w-5 h-5 text-[#D4AF37] mr-2" />
                    <span>contact@eagles-way.com</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AboutUs;
